import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { array, arrayOf, bool, func, number, object, shape, string, oneOfType } from 'prop-types';
import { Button, Select } from '../../../../../ps-aspect-design';
import { getDefaultAppliedGeoDiffs, mergeDiffs, transformDiffValues } from '../helpers';
import { numberWithCommas } from 'ps-components/lib/formatters';
import classnames from 'classnames';
import InPlaceIndicator from 'ps-components/LoadingIndicator/InPlaceIndicator/InPlaceIndicator';
import Pagination from 'ps-components/Pagination/Pagination';
import Placeholder from 'ps-components/Placeholder/Placeholder';
import Search from 'ps-components/Search/Search';

import {
  getReviewImpact,
  getReviewImpactSelectedView,
  getReviewImpactViews,
  reviewImpactLoadMore,
  setLoading,
  setReviewImpactSelectedView,
  toggleLocation
} from './ReviewImpactActions';
import { PAGE, PAGE_CODE, CATEGORY_CODE } from '../../../../../lib/pageCodeDefaults';
import { reviewImpactGridActions } from './ReviewImpactGrid/ReviewImpactGridCreate';
import { showSlideout } from '../../../../Slideout/SlideoutReducer';
import { SLIDEOUTS as MP_SLIDEOUTS } from '../../../../../components/Slideout/Slideouts';
import { Tooltip } from 'ps-design';
import Dropdown from '../../../../Dropdown/Dropdown';
import DropdownViews from '../../../../Dropdown/DropdownViews';
import EmployeeFilter from '../EmployeeFilter/EmployeeFilter';
import ReviewImpactGrid from './ReviewImpactGrid/ReviewImpactGrid';

import './ReviewImpact.scss';

ReviewImpact.propTypes = {
  actions: shape({
    getReviewImpact: func,
    reviewImpactLoadMore: func,
    toggleLocation: func
  }),
  appliedGeoDiffs: shape({
    location: string,
    differential: number
  }),
  applyGeoDiffs: func,
  applyLocationsLoading: bool,
  employeeFilters: arrayOf(
    shape({
      element: string,
      operator: string,
      value: oneOfType([number, string]),
      type: string,
      sourceField: string,
      dataSource: string
    })
  ),
  fieldFormatting: shape({
    numericRounding: number,
    monetaryRounding: number,
    percentRounding: number
  }),
  getCompanyLocations: func,
  grid: object,
  gridActions: object,
  hasGeoOverrides: bool,
  isSavingGeoDifferentials: bool,
  maid: string,
  replacementTokens: array,
  returnToEdit: func,
  reviewGeoDifferential: shape({
    employeeData: array,
    loaded: bool,
    loading: bool,
    locationData: array,
    rowMetadata: arrayOf(
      shape({
        count: number,
        expanded: bool,
        loading: bool,
        location: string,
        noMore: bool
      })
    ),
    totalNumberOfEmployees: number,
    totalNumberOfLocations: number
  }),
  settings: shape({
    compareWithLocation: string,
    internalData: string,
    trendRange: number,
    trendRangeType: string
  }),
  useJobCodeKey: bool,
  readOnlyUser: bool,
  userRights: object,
  groupAdminCanConfigureViews: bool,
  regularUserCanConfigureViews: bool
};

function ReviewImpact(props) {
  const LOCATION_PAGINATION_LIMIT = 10;
  const DEFAULT_EMPLOYEE_LIMIT = 3;
  const MORE_EMPLOYEES_LIMIT = 50;
  // search by location, jobTitle, or employee
  const [searchType, setSearchType] = useState('location');
  const [searchText, setSearchText] = useState('');
  const [locationOffset, setLocationOffset] = useState(0);
  const [orderBy, setOrderBy] = useState(null);
  const [employeeFilters, setEmployeeFilters] = useState(props.employeeFilters);
  const [reloadDiffs, setReloadDiffs] = useState(false);
  const [selectedView, setSelectedView] = useState(props.reviewGeoDifferential.reviewImpactSelectedView);
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (!props.reviewGeoDifferential.loading) {
      // make sure to reload data on first page when locationType or searchText changes
      // note: setting location offset to 0 will trigger the next useEffect to reload data
      setLocationOffset(new Number(0));
    }
  }, [searchText, employeeFilters]);

  useEffect(() => {
    if (!props.reviewGeoDifferential.loading) {
      setReloadDiffs(true);
      setLocationOffset(new Number(0));
    }
  }, [props.settings.internalData, props.settings.compareWithLocation]);

  useEffect(() => {
    const viewData = props.reviewGeoDifferential.reviewImpactSelectedView;
    if (!viewData.loading && viewData.selectedViewPk !== selectedView.selectedViewPk) {
      if (selectedView.selectedViewPk) {
        setLocationOffset(new Number(0));
      }
      setSelectedView(props.reviewGeoDifferential.reviewImpactSelectedView);
    }
  }, [props.reviewGeoDifferential.reviewImpactSelectedView]);

  useEffect(() => {
    if (props.employeeFilters !== employeeFilters) {
      setReloadDiffs(true);
      setEmployeeFilters(props.employeeFilters);
    }
  }, [props.employeeFilters]);

  useEffect(() => {
    if (!props.reviewGeoDifferential.loading) {
      reloadData(locationOffset);
    }
  }, [locationOffset, orderBy]);

  useEffect(() => {
    props.actions.getReviewImpactViews().then(() => {
      props.actions.getReviewImpactSelectedView();
    });
  }, []);

  function reloadData(pageOffset) {
    const params = {
      employeeFilters: employeeFilters,
      employeeLimit: DEFAULT_EMPLOYEE_LIMIT,
      employeeNameSearch: searchType === 'employee' ? searchText : '',
      geoDifferentials: props.appliedGeoDiffs,
      jobTitleSearch: searchType === 'jobTitle' ? searchText : '',
      locationLimit: LOCATION_PAGINATION_LIMIT,
      locationOffset: pageOffset,
      locationSearch: searchType === 'location' ? searchText : '',
      locationType: props.settings?.internalData === 'workLocation' ? 'work-location' : 'city-state',
      orderBy
    };

    if (reloadDiffs) {
      props.actions.setLoading();
      props.getCompanyLocations().then(resp => {
        const defaultAppliedGeoDiffs = transformDiffValues(getDefaultAppliedGeoDiffs(resp?.applyLocations));

        props.actions.getReviewImpact(props.maid, {
          ...params,
          geoDifferentials: mergeDiffs(defaultAppliedGeoDiffs, props.appliedGeoDiffs)
        });
      });
    } else {
      props.actions.getReviewImpact(props.maid, params);
    }
    setReloadDiffs(false);
  }

  // scrollRef used to scroll up to top after search or pagination
  const scrollRef = useRef();
  const changeSearchValue = useRef();

  const hasJobCodeKeyReplacement = props.replacementTokens?.find(value => value.token === 'job_code_key_text');
  const jobCodeKeyText = hasJobCodeKeyReplacement?.value || 'Job Code Key';

  const locationType = props.settings?.internalData || 'workLocation';

  const showNoResultsAfterSearch =
    searchText &&
    !props.reviewGeoDifferential.loading &&
    props.reviewGeoDifferential.loaded &&
    props.reviewGeoDifferential.employeeData == null;

  const showNoResultsAfterEmployeeFilters =
    employeeFilters?.length > 0 &&
    !props.reviewGeoDifferential.loading &&
    props.reviewGeoDifferential.loaded &&
    props.reviewGeoDifferential.employeeData == null;

  const showNoEmployeesFound =
    !showNoResultsAfterEmployeeFilters &&
    !searchText &&
    !props.reviewGeoDifferential.loading &&
    props.reviewGeoDifferential.loaded &&
    props.reviewGeoDifferential.employeeData == null;

  const showNoLocationsFound =
    !searchText &&
    !props.reviewGeoDifferential.loading &&
    props.reviewGeoDifferential.loaded &&
    props.reviewGeoDifferential.locationData == null;

  function scrollAndSearch(searchText) {
    if (scrollRef?.current?.scrollTop) {
      scrollRef.current.scrollTop = 0; // reset scroll
    }
    // create a new string to trigger re-render, so you can change the
    // search type and still search with the same input
    setSearchText(new String(searchText));
    setLocationOffset(0);
  }

  // Pagination logic for locations
  function pageLocations(_limit, offset) {
    setLocationOffset(offset);
  }

  // clicking x to clear search text
  function searchOnClick() {
    scrollAndSearch('');
  }

  // when pressing enter to execute a search
  function searchOnKeyDown(event) {
    if (event.key === 'Enter') {
      scrollAndSearch(event.target.value);
    }
  }

  // clicking the search icon to execute a search
  function searchOnSearch(value) {
    scrollAndSearch(value);
  }

  function getSearchInput() {
    let searchPlaceholderText;
    if (searchType === 'location') {
      if (locationType === 'workLocation') {
        searchPlaceholderText = 'Search by Work Location';
      } else {
        searchPlaceholderText = 'Search by City/State Location';
      }
    } else if (searchType === 'jobTitle') {
      searchPlaceholderText = 'Search by Job Title';
    } else {
      searchPlaceholderText = 'Search by Employee Name';
    }

    return (
      <>
        <Search
          className="review-impact__search"
          onClick={searchOnClick}
          onKeyDown={searchOnKeyDown}
          onSearch={searchOnSearch}
          placeholder={searchPlaceholderText}
          ref={changeSearchValue}
          useInternalState
        />

        <Tooltip
          className="system-default-icon"
          hoverText="Searching will not affect which employees are impacted by geo differentials. Use Filter Employees to specify which employees should receive a differential."
          id="system-default-icon"
          position="bottom"
        />
      </>
    );
  }

  function getLocationDropdown() {
    const options = [
      { key: 'location', text: 'Location' },
      { key: 'employee', text: 'Employee' },
      { key: 'jobTitle', text: 'Job Title' }
    ];

    return (
      <Select
        className="review-impact__dropdown"
        value={options.find(o => o.key === searchType)?.text || 'Select Search Type'}
        options={options}
        handleChange={value => {
          const option = options.find(o => o.text === value);
          setSearchType(option.key);
        }}
        componentRef={dropdownRef}
      />
    );
  }

  function loadMoreEmployees(location, currentCount) {
    const employeeOffset = currentCount;

    props.actions.reviewImpactLoadMore(props.maid, {
      employeeFilters,
      employeeLimit: MORE_EMPLOYEES_LIMIT,
      employeeNameSearch: searchType === 'employee' ? searchText : '',
      employeeOffset,
      geoDifferentials: props.appliedGeoDiffs,
      jobTitleSearch: searchType === 'jobTitle' ? searchText : '',
      location,
      locationType: props.settings?.internalData === 'workLocation' ? 'work-location' : 'city-state',
      orderBy
    });
  }

  function onSortChange(sortBy) {
    if (sortBy && sortBy.length > 0) {
      const sort = sortBy[0].desc ? 'desc' : 'asc';
      // remove the 'field_' suffix and (for geo diff columns) the trailing numbers from the id
      let orderById = sortBy[0].id?.replace('field_', '');
      let term = props.reviewGeoDifferential?.employeeData?.terms?.find(t => t.term === orderById);
      if (!term) {
        // search again without the number suffix
        term = props.reviewGeoDifferential?.employeeData?.terms?.find(t => t.term === orderById.replace(/_\d+$/, ''));
      }

      let orderByTerm = term?.fieldName || orderById;
      if (term?.sqlColumn) {
        orderByTerm = term.sqlColumn;
      } else if (term?.tableName && term?.fieldName) {
        orderByTerm = `${term.tableName}.${term.fieldName}`;
      }

      setOrderBy({ term: orderByTerm, sort });
    } else {
      setOrderBy(null);
    }
  }

  function getSelectedViewName(viewPk) {
    const selectedView = props.reviewGeoDifferential.reviewImpactViews?.views?.find(v => v.view_pk === viewPk);

    return props?.grid?.metadata?.viewName || selectedView?.view_name;
  }

  function showConfigureViewSlideout() {
    props.actions.showSlideout(MP_SLIDEOUTS.CONFIGURE_VIEW, {
      onRefresh: refreshViews,
      categoryCode: CATEGORY_CODE.REVIEW_IMPACT,
      page: PAGE.REVIEW_IMPACT,
      pageCode: PAGE_CODE.REVIEW_IMPACT,
      pageName: 'Review Impact Views',
      onRefreshData: () => setLocationOffset(new Number(0)),
      includeAggregates: false
    });
  }

  function refreshViews() {
    props.actions.getReviewImpactViews().then(() => {
      props.actions.getReviewImpactSelectedView();
    });
  }

  function setSelectedReviewImpactView(view) {
    const selectedView = props.reviewGeoDifferential.reviewImpactViews?.views?.find(
      l => l.view_name?.trim() === view?.trim()
    );

    if (selectedView) {
      props.actions.setReviewImpactSelectedView(selectedView.view_pk);
    }
  }

  return (
    <>
      <div className="review-impact__container" ref={scrollRef}>
        {props.reviewGeoDifferential?.loading && !props.reviewGeoDifferential?.loaded ? (
          <InPlaceIndicator />
        ) : (
          <>
            {(showNoResultsAfterEmployeeFilters || (!showNoEmployeesFound && !showNoLocationsFound)) && (
              <>
                <div className="review-impact__title-row">
                  <div className="review-impact__title-section">
                    <div className="review-impact__title">Apply Geo Differentials to market composites and ranges</div>
                    <div className="review-impact__sub-title">
                      {!props.reviewGeoDifferential?.loading &&
                        `Impact \
                        ${numberWithCommas(props.reviewGeoDifferential?.totalNumberOfEmployees || 0)} \
                        employees in \
                        ${numberWithCommas(props.reviewGeoDifferential?.totalNumberOfLocations || 0)} \
                        locations`}
                    </div>
                  </div>

                  <div className="review-impact__filter-section">
                    <EmployeeFilter />
                  </div>
                </div>

                <div className="review-impact__search-row">
                  <div className="review-impact__search-section">
                    {getLocationDropdown()}
                    {getSearchInput()}
                  </div>
                  <div className="review-impact__dropdown-group">
                    <label>View</label>
                    <Dropdown
                      className={classnames('review-impact__view-dropdown', { restricted: props.restrictedUser === 1 })}
                      text={getSelectedViewName(props.reviewGeoDifferential.reviewImpactSelectedView?.selectedViewPk)}
                      customRenderComponent={DropdownViews}
                      customRenderProps={{
                        views: props.reviewGeoDifferential.reviewImpactViews?.views,
                        selectedViewPk: props.reviewGeoDifferential.reviewImpactSelectedView?.selectedViewPk,
                        onEdit: showConfigureViewSlideout,
                        onSelectView: setSelectedReviewImpactView,
                        userRights: props.userRights,
                        groupAdminCanConfigureViews: props.groupAdminCanConfigureViews,
                        regularUserCanConfigureViews: props.regularUserCanConfigureViews
                      }}
                    />
                  </div>
                </div>
              </>
            )}

            <>
              {showNoResultsAfterEmployeeFilters ? (
                <Placeholder
                  className="pxl-text-small pxl-text-muted"
                  primaryText="No results found with applied filters"
                  secondaryText="Please modify filters and try again"
                  showText={true}
                />
              ) : showNoResultsAfterSearch ? (
                <Placeholder
                  className="pxl-text-small pxl-text-muted"
                  primaryText={`No results found for '${searchText}'`}
                  secondaryText="Please try again with a different search"
                  showText={true}
                />
              ) : showNoEmployeesFound ? (
                <Placeholder
                  className="pxl-text-small pxl-text-muted"
                  secondaryText={`In order to apply geo differentials to your company locations, please upload employee data first`}
                  showText={true}
                />
              ) : (
                showNoLocationsFound && (
                  <Placeholder
                    className="pxl-text-small pxl-text-muted"
                    primaryText={`No location data found`}
                    secondaryText={`Location matching by ${
                      locationType === 'workLocation' ? 'Work Location' : 'City/State'
                    }`}
                    showText={true}
                  />
                )
              )}

              {!showNoResultsAfterEmployeeFilters &&
                !showNoResultsAfterSearch &&
                !showNoEmployeesFound &&
                !showNoLocationsFound && (
                  <ReviewImpactGrid
                    employeeData={props.reviewGeoDifferential.employeeData}
                    history={props.history}
                    jobCodeKeyText={jobCodeKeyText}
                    loading={props.reviewGeoDifferential?.loading || props.applyLocationsLoading}
                    loadMoreEmployees={loadMoreEmployees}
                    locationData={props.reviewGeoDifferential.locationData}
                    locationType={locationType}
                    numericRounding={props.fieldFormatting.numericRounding}
                    onSortChange={onSortChange}
                    percentRounding={props.fieldFormatting.percentRounding}
                    reviewImpactGrid={props.grid}
                    reviewImpactGridActions={props.gridActions}
                    rowMetadata={props.reviewGeoDifferential.rowMetadata}
                    toggleLocation={props.actions.toggleLocation}
                    useJobCodeKey={props.useJobCodeKey}
                  />
                )}
            </>
          </>
        )}
      </div>

      <div className="pulse-apply-geo__footer-container review-impact__footer">
        {!props.reviewGeoDifferential?.loading &&
          (showNoResultsAfterEmployeeFilters || (!showNoEmployeesFound && !showNoLocationsFound)) && (
            <>
              <Button
                className="footer-button"
                data-testid="edit-geo-button"
                icon="Left"
                onClick={props.returnToEdit}
                variant="secondary"
              >
                Back to Edit
              </Button>

              <div className="review-impact__pagination-container">
                <Pagination
                  className="review-impact__pagination"
                  offset={locationOffset}
                  pageAction={pageLocations}
                  pageShowRange={3}
                  scrollEl={scrollRef}
                  scrollTopPageChange
                  step={LOCATION_PAGINATION_LIMIT}
                  totalCount={props.reviewGeoDifferential?.totalPaginationLocations}
                />
              </div>

              {!showNoResultsAfterEmployeeFilters && (
                <Button
                  className="footer-button apply-button"
                  disabled={!props.hasGeoOverrides || props.isSavingGeoDifferentials || props.readOnlyUser}
                  data-testid="apply-geo-button"
                  onClick={props.applyGeoDiffs}
                  variant="primary"
                >
                  {props.isSavingGeoDifferentials ? 'Applying...' : 'Apply Geo Differential'}
                </Button>
              )}
            </>
          )}
      </div>
    </>
  );
}

const mapStateToProps = ({ geoDifferential, reviewGeoDifferential, session }) => ({
  applyLocationsLoading: geoDifferential.applyLocationsLoading,
  employeeFilters: geoDifferential.employeeFilters,
  fieldFormatting: {
    numericRounding: session.user.preferences.numericRounding,
    monetaryRounding: session.user.preferences.monetaryRounding,
    percentRounding: session.user.preferences.percentRounding
  },
  grid: reviewGeoDifferential.reviewImpactGridReducer,
  maid: session.organization.masterAccountId,
  replacementTokens: session.replacementTokens,
  restrictedUser: session.userRights?.mp_ru_user_p,
  reviewGeoDifferential: reviewGeoDifferential.reviewImpactReducer,
  settings: geoDifferential.settings,
  useJobCodeKey: session.organization.useJobCodeKey,
  userRights: session.user.rights,
  groupAdminCanConfigureViews: session.groupAdminCanConfigureViews,
  regularUserCanConfigureViews: session.regularUserCanConfigureViews
});

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators(
    {
      getReviewImpact,
      getReviewImpactSelectedView,
      getReviewImpactViews,
      reviewImpactLoadMore,
      setLoading,
      setReviewImpactSelectedView,
      showSlideout,
      toggleLocation
    },
    dispatch
  ),
  gridActions: bindActionCreators(
    {
      ...reviewImpactGridActions
    },
    dispatch
  )
});

export default connect(mapStateToProps, mapDispatchToProps)(ReviewImpact);
